// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useProductContext from "@store/Framework/Store/Data/Products/ProductsHooks/useProductContext";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";

import useCurrentBreakpointIndex from "@store/Framework/Common/Hooks/useCurrentBreakpointIndex";
import ProductImageCarousel, { ProductImageCarouselStyles } from "@store/Overrides/Components/ProductImageCarousel";
import BunzlZoomOut from "@store/Overrides/Widgets/ProductDetails/BunzlZoomOut";
import useCurrentBreakpointValue from "@toolkit/Framework/Common/Hooks/useCurrentBreakpointValue";
import { useBrandName } from "@toolkit/Framework/Store/Toolkit/Pages/ProductList/ProductListHooks";
import { css } from "styled-components";

interface ProductDetailsCarouselButtonsStyles {
    prevArrowButton?: ButtonProps;
    nextArrowButton?: ButtonProps;
    zoomButtonCarouselWrapper?: InjectableCss;
    buttonRowWrapper?: InjectableCss;
    zoomButton?: ButtonProps;
    prevNextButtonsWrapper?: InjectableCss;
    carouselStyles?: ProductImageCarouselStyles;
    carouselWrapper?: InjectableCss;
}

interface Props {
    verticalThumbnails?: boolean;
    selectedImageIndex: number;
    setSelectedImageIndex: (index: number) => void;
    handleZoomClick?: () => void;
    numberOfImagesToShow: number;
    numberOfImages: number;
}

const getProductDetailsButtonStyles = (icon: React.ComponentType, size: number, iconSize: number): ButtonProps => {
    return {
        sizeVariant: "large",
        variant: "tertiary",
        icon: {
            position: "left",
            src: icon,
        },
        css: css`
            color: ${getColor("text.linkDark")};
            position: relative;
            display: flex;
            height: ${pxToRem(size)};
            width: ${pxToRem(size)};
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            border: 1px solid ${getColor("common.border")};
            box-shadow: ${({ theme }) => theme.shadows[1]};
            ${IconWrapper} {
                color: ${getColor("text.link")};
                :hover {
                    svg {
                        color: ${getColor("text.linkHover")};
                    }
                }
                width: ${pxToRem(size)};
                height: ${pxToRem(size)};
                top: 0;
                margin: 0;
                svg {
                    color: ${getColor("text.linkHover")};
                    height: ${pxToRem(iconSize)};
                    width: ${pxToRem(iconSize)};
                }
            }
        `,
    };
};

export const productDetailsCarouselButtonsStyles: ProductDetailsCarouselButtonsStyles = {
    carouselStyles: {
        mainContainer: {
            offsetCss: css`
                align-items: center;
                justify-content: center;
            `,
        },

        carouselGridItem: {
            width: 10,
        },
        nextArrowGridItem: {
            css: css`
                display: none;
            `,
        },
        prevArrowGridItem: {
            css: css`
                display: none;
            `,
        },
        carouselSlidesContainer: {
            css: css`
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100%;
            `,
        },
    },
    prevArrowButton: getProductDetailsButtonStyles(ChevronLeft, 48, 24),
    nextArrowButton: getProductDetailsButtonStyles(ChevronRight, 48, 24),
    zoomButton: getProductDetailsButtonStyles(BunzlZoomOut, 48, 18),
    prevNextButtonsWrapper: {
        css: css`
            display: flex;
            gap: ${pxToRem(4)};
        `,
    },
    buttonRowWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
        `,
    },
    carouselWrapper: {
        css: css`
            max-width: 75%;
            min-width: 50%;
        `,
    },
};

const ProductDetailsCarouselButtons: React.FC<Props> = ({
    selectedImageIndex,
    setSelectedImageIndex,
    verticalThumbnails = false,
    handleZoomClick,
    numberOfImagesToShow,
    numberOfImages,
}) => {
    const styles = productDetailsCarouselButtonsStyles;

    const shouldShowCarousel = numberOfImages > 1;
    const handleNextArrowClick = () => {
        if (selectedImageIndex < numberOfImages - 1) {
            setSelectedImageIndex(selectedImageIndex + 1);
        } else {
            setSelectedImageIndex(0);
        }
    };

    const handlePrevArrowClick = () => {
        if (selectedImageIndex > 0) {
            setSelectedImageIndex(selectedImageIndex - 1);
        } else {
            setSelectedImageIndex(numberOfImages - 1);
        }
    };
    return (
        <StyledWrapper {...styles.buttonRowWrapper} className="product-details-carousel-buttons">
            <Button
                {...styles.zoomButton}
                onClick={handleZoomClick}
                aria-label="Zoom"
                className="product-details-carousel-zoom-button"
            />
            {!verticalThumbnails && shouldShowCarousel && (
                <StyledWrapper {...styles.carouselWrapper} className="product-details-horizontal-carousel-wrapper">
                    <ProductImageCarousel
                        selectedIndex={selectedImageIndex}
                        scrollingBehavior="horizontal"
                        numberOfVisibleThumbnails={numberOfImagesToShow}
                        onSelectImage={setSelectedImageIndex}
                        extendedStyles={styles.carouselStyles}
                    />
                </StyledWrapper>
            )}
            {shouldShowCarousel && (
                <StyledWrapper
                    {...styles.prevNextButtonsWrapper}
                    className="product-details-carousel-prev-next-wrapper"
                >
                    <Button
                        {...styles.prevArrowButton}
                        onClick={handlePrevArrowClick}
                        className="product-details-carousel-prev-button"
                    />
                    <Button
                        {...styles.nextArrowButton}
                        onClick={handleNextArrowClick}
                        className="product-details-carousel-next-button"
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};
export const defaultProductDetailsCarouselButtonsCss = `.product-details-carousel-buttons {
}
.product-details-carousel-zoom-button {
}
.product-details-horizontal-carousel-wrapper {
}
.product-details-carousel-prev-next-wrapper {
}
.product-details-carousel-prev-button {
}
.product-details-carousel-next-button {
}
`;

export default ProductDetailsCarouselButtons;
